import type React from 'react';
import { useLocale } from '../../lib/hooks/use-locale.ts';

export type FormatPercentageOptions = {
    locale: string;
    decimals?: number;
    showEmptyIfZero?: boolean;
    emptyText?: string;
    showPlusSignIfPositive?: boolean;
};

/**
 * Pure function to format a number as a percentage string.
 */
export const formatPercentage = (
    value: number | string | undefined | null,
    {
        locale,
        decimals = 1,
        emptyText = '-',
        showEmptyIfZero = false,
        showPlusSignIfPositive = false,
    }: FormatPercentageOptions,
): string => {
    const cleanValue = Number(value);

    if (Number.isNaN(cleanValue)) {
        return showEmptyIfZero ? emptyText : '0%';
    } else if (cleanValue === 0 && showEmptyIfZero) {
        return emptyText;
    } else {
        return new Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            signDisplay: showPlusSignIfPositive ? 'always' : 'auto',
        }).format(cleanValue);
    }
};

export type ColorThreshold = {
    min: number;
    color: string;
};

const getThresholdColor = (
    value: number | string | undefined | null,
    thresholds: ColorThreshold[],
): string | undefined => {
    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
        return undefined;
    }
    const sorted = [...thresholds].toSorted((a, b) => b.min - a.min);
    return sorted.find((t) => numValue >= t.min)?.color;
};

type Props = {
    children: number | string | undefined | null;
    colorThresholds?: ColorThreshold[];
} & Omit<FormatPercentageOptions, 'locale'>;

export const PercentageOrEmpty: React.FC<Props> = ({
    children,
    decimals = 1,
    emptyText = '-',
    showEmptyIfZero = false,
    showPlusSignIfPositive = false,
    colorThresholds,
}) => {
    const { locale } = useLocale();

    const formatted = formatPercentage(children, {
        locale,
        decimals,
        emptyText,
        showEmptyIfZero,
        showPlusSignIfPositive,
    });

    if (colorThresholds) {
        const color = getThresholdColor(children, colorThresholds);
        return <span className={color}>{formatted}</span>;
    }

    return <>{formatted}</>;
};
